import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

/**
 * ProductSuppliers Component
 * 
 * This manages the product_suppliers linking table which connects:
 * - products (Many-to-One via product_id foreign key)
 * - suppliers (Many-to-One via supplier_id foreign key)
 * 
 * The linking table stores relationship-specific data like:
 * - supplier_sku: The supplier's SKU for this product
 * - unit_price: The price this supplier charges
 * - lead_time_days: Delivery time from this supplier
 * - min_order_qty: Minimum order quantity
 * - is_preferred: Whether this is the preferred supplier
 */
const ProductSuppliers = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSuppliers, setProductSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [columnSettings, setColumnSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnMenu, setShowColumnMenu] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    supplier_id: '',
    supplier_sku: '',
    unit_price: '',
    lead_time_days: 0,
    min_order_qty: 1,
    is_preferred: false,
    is_active: true
  });

  useEffect(() => {
    if (organization) {
      const loadData = async () => {
        loadColumnSettings();
        await loadProducts();
        await loadSuppliers();
      };
      loadData();
    }
  }, [organization]);

  // Handle resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = e.clientX;
      if (newWidth >= 200 && newWidth <= 600) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    if (selectedProduct) {
      loadProductSuppliers(selectedProduct);
    }
  }, [selectedProduct]);

  // Load column settings from localStorage
  const loadColumnSettings = () => {
    const saved = localStorage.getItem(`productSuppliersColumns_${organization}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed.settings || {});
        setSortConfig(parsed.sort || { key: null, direction: 'asc' });
        return parsed.settings || {};
      } catch (e) {
        console.error('Error loading column settings:', e);
        return {};
      }
    }
    return {};
  };

  // Save column settings to localStorage
  const saveColumnSettings = (settings, sort) => {
    localStorage.setItem(`productSuppliersColumns_${organization}`, JSON.stringify({
      settings,
      sort
    }));
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
      if (productsData.length > 0) {
        setSelectedProduct(productsData[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await inventoryApi.getSuppliers(organization);
      const suppliersData = Array.isArray(response) ? response : (response.data || []);
      setSuppliers(suppliersData);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
    }
  };

  const loadProductSuppliers = async (productId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getProductSuppliers(productId);
      const productSuppliersData = Array.isArray(response) ? response : (response.data || []);
      setProductSuppliers(productSuppliersData);

      // Generate dynamic columns based on the first product supplier
      if (productSuppliersData.length > 0) {
        const saved = localStorage.getItem(`productSuppliersColumns_${organization}`);
        let savedSettings = {};
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            savedSettings = parsed.settings || {};
          } catch (e) {
            console.error('Error parsing saved settings:', e);
          }
        }

        const firstItem = productSuppliersData[0];
        const columns = Object.keys(firstItem)
          .filter(key => key !== 'id' && key !== 'product_id' && key !== 'productId')
          .map((key, index) => ({
            key,
            label: formatColumnLabel(key),
            visible: savedSettings[key]?.visible !== false,
            order: savedSettings[key]?.order ?? index
          }));
        columns.sort((a, b) => a.order - b.order);
        setTableColumns(columns);
      }
    } catch (err) {
      setError(err.message || 'Failed to load product suppliers');
      setProductSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format column labels
  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key) => {
    const newSettings = {
      ...columnSettings,
      [key]: {
        ...columnSettings[key],
        visible: !(columnSettings[key]?.visible ?? true)
      }
    };
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);

    setTableColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Move column up in order
  const moveColumnUp = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex <= 0) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex - 1], newColumns[currentIndex]] =
      [newColumns[currentIndex], newColumns[currentIndex - 1]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Move column down in order
  const moveColumnDown = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex >= tableColumns.length - 1) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex], newColumns[currentIndex + 1]] =
      [newColumns[currentIndex + 1], newColumns[currentIndex]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    saveColumnSettings(columnSettings, newSortConfig);
  };

  // Sort product suppliers
  const sortedProductSuppliers = React.useMemo(() => {
    let sortable = [...productSuppliers];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortable;
  }, [productSuppliers, sortConfig]);

  // Filter product suppliers
  const filteredProductSuppliers = sortedProductSuppliers.filter(ps => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return Object.values(ps).some(value =>
      value?.toString().toLowerCase().includes(term)
    );
  });

  // Get visible columns
  const visibleColumns = tableColumns.filter(col => col.visible);

  // Reset column settings
  const resetColumnSettings = () => {
    localStorage.removeItem(`productSuppliersColumns_${organization}`);
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
    if (selectedProduct) {
      loadProductSuppliers(selectedProduct);
    }
  };

  // Helper function to format cell values
  const formatCellValue = (value, key) => {
    if (value === null || value === undefined) return '-';

    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Format price/cost fields
    if ((key.includes('price') || key.includes('cost')) && typeof value === 'number') {
      return `$${value.toFixed(2)}`;
    }

    // Format dates
    if (key.includes('date') || key.includes('created') || key.includes('updated')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (e) {
        return value;
      }
    }

    // Format supplier_id to show supplier name
    if (key === 'supplier_id' || key === 'supplierId') {
      return getSupplierName(value);
    }

    return value.toString();
  };

  const handleOpenModal = (productSupplier = null) => {
    if (productSupplier) {
      setEditingId(productSupplier.id);
      setEditFormData({
        supplier_id: productSupplier.supplier_id || productSupplier.supplierId || '',
        supplier_sku: productSupplier.supplier_sku || productSupplier.supplierSku || '',
        unit_price: productSupplier.unit_price || productSupplier.unitPrice || '',
        lead_time_days: productSupplier.lead_time_days || productSupplier.leadTimeDays || 0,
        min_order_qty: productSupplier.min_order_qty || productSupplier.minOrderQty || 1,
        is_preferred: productSupplier.is_preferred !== undefined ? productSupplier.is_preferred : (productSupplier.isPreferred || false),
        is_active: productSupplier.is_active !== undefined ? productSupplier.is_active : (productSupplier.isActive !== undefined ? productSupplier.isActive : true)
      });
    } else {
      setEditingId(null);
      setEditFormData({
        supplier_id: '',
        supplier_sku: '',
        unit_price: '',
        lead_time_days: 0,
        min_order_qty: 1,
        is_preferred: false,
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!selectedProduct) {
      setError('Please select a product first');
      return;
    }

    try {
      setError(null);
      const data = {
        ...editFormData,
        unit_price: parseFloat(editFormData.unit_price) || 0,
        lead_time_days: parseInt(editFormData.lead_time_days) || 0,
        min_order_qty: parseInt(editFormData.min_order_qty) || 1
      };

      if (editingId) {
        await inventoryApi.updateProductSupplier(selectedProduct, editingId, data);
      } else {
        await inventoryApi.createProductSupplier(selectedProduct, data);
      }
      await loadProductSuppliers(selectedProduct);
      handleCloseModal();
    } catch (err) {
      setError(err.message || 'Failed to save product supplier');
    }
  };

  const handleDelete = async (productSupplierId) => {
    if (!window.confirm('Are you sure you want to delete this product-supplier relationship?')) {
      return;
    }

    try {
      setError(null);
      await inventoryApi.deleteProductSupplier(selectedProduct, productSupplierId);
      await loadProductSuppliers(selectedProduct);
    } catch (err) {
      setError(err.message || 'Failed to delete product supplier');
    }
  };

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.name : 'Unknown';
  };

  if (loading && products.length === 0) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_products')}
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="pm-page-card">
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
        <button className="pm-btn-primary" onClick={() => { loadProducts(); loadSuppliers(); }}>
          {t('inventory.retry_button')}
        </button>
      </div>
    );
  }

  // Filter products for the side list
  const filteredProducts = products.filter(product => {
    if (!productSearchTerm) return true;
    const term = productSearchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(term) ||
      product.sku?.toLowerCase().includes(term) ||
      product.SKU?.toLowerCase().includes(term)
    );
  });

  const selectedProductData = products.find(p => p.id === selectedProduct);

  return (
    <div className="pm-page-card pm-split-layout" style={{ '--sidebar-width': `${sidebarWidth}px` }}>
      {/* Product Selection Sidebar */}
      <div className="pm-sidebar">
        <h3>{t('inventory.products_heading')}</h3>
        <div className="pm-sidebar-search">
          <input
            type="search"
            placeholder={t('inventory.search_products_placeholder')}
            value={productSearchTerm}
            onChange={(e) => setProductSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-product-list-wrap">
          {filteredProducts.length === 0 ? (
            <div className="pm-empty-state compact">
              <p>{t('inventory.no_products_found')}</p>
            </div>
          ) : (
            <ul className="pm-product-list">
              {filteredProducts.map(product => (
                <li 
                  key={product.id}
                  onClick={() => setSelectedProduct(product.id)}
                  className={`pm-product-list-item ${selectedProduct === product.id ? 'selected' : ''}`}
                >
                  <div className="pm-product-list-name">{product.name}</div>
                  <div className="pm-product-list-sku">
                    {t('inventory.sku_prefix')} {product.sku || product.SKU || 'N/A'}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Resizer */}
      <div
        onMouseDown={() => setIsResizing(true)}
        className={`pm-resizer ${isResizing ? 'resizing' : ''}`}
      >
        <div className="pm-resizer-icon">⋮</div>
      </div>

      {/* Main Content Area */}
      <div className="pm-main-panel">
        <div className="pm-header">
          <h3 className="pm-header-title">
            {t('inventory.product_suppliers_title')}
            {selectedProductData && (
              <span className="pm-context-title">
                {t('inventory.product_variants_for')} {selectedProductData.name}
              </span>
            )}
          </h3>
          <button className="pm-btn-primary" onClick={() => handleOpenModal()} disabled={!selectedProduct}>
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.add_supplier_button')}</span>
          </button>
        </div>

        {error && (
          <div className="pm-error-banner">
            <span aria-hidden="true">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <div className="pm-toolbar">
          <div className="pm-search-wrap">
            <input
              type="search"
              placeholder={t('inventory.search_product_suppliers_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {!selectedProduct ? (
          <div className="pm-empty-state">
            <div className="pm-empty-state-icon" aria-hidden="true">📦</div>
            <p>{t('inventory.select_product_suppliers_message')}</p>
          </div>
        ) : productSuppliers.length === 0 && !loading ? (
          <div className="pm-empty-state">
            <div className="pm-empty-state-icon" aria-hidden="true">🏭</div>
            <h3>{t('inventory.no_suppliers_for_product')}</h3>
            <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
              <span aria-hidden="true">➕</span>
              {t('inventory.add_supplier_button')}
            </button>
          </div>
        ) : (
          <div className="pm-table-wrap">
            <table className="pm-table" role="grid">
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>
                    <div className="pm-header-content">
                      {tableColumns.filter(c => !c.visible).length > 0 && (
                        <button
                          className="pm-column-btn visibility-btn"
                          onClick={() => setShowColumnMenu(showColumnMenu === 'hidden' ? null : 'hidden')}
                          title={t('inventory.show_hidden_columns')}
                        >
                          ⊕
                        </button>
                      )}
                      <div className="pm-header-label">{t('inventory.actions_header')}</div>
                    </div>
                    {showColumnMenu === 'hidden' && (
                      <div className="pm-column-menu">
                        <div className="menu-header">{t('inventory.hidden_columns_label')}</div>
                        {tableColumns.filter(c => !c.visible).map(col => (
                          <button key={col.key} onClick={() => {
                            toggleColumnVisibility(col.key);
                          }}>
                            {t('inventory.show_column')} {col.label}
                          </button>
                        ))}
                        <hr />
                        <button onClick={() => {
                          resetColumnSettings();
                          setShowColumnMenu(null);
                        }}>
                          {t('inventory.reset_columns')}
                        </button>
                      </div>
                    )}
                  </th>
                  {visibleColumns.map((col, index) => (
                    <th key={col.key} className="pm-sortable-header">
                      <div className="pm-header-content">
                        <div className="pm-header-controls">
                          <button
                            className="pm-column-btn"
                            onClick={() => moveColumnUp(col.key)}
                            disabled={index === 0}
                            title={t('inventory.move_left_tooltip')}
                          >
                            ◀
                          </button>
                          <button
                            className="pm-column-btn"
                            onClick={() => moveColumnDown(col.key)}
                            disabled={index === visibleColumns.length - 1}
                            title={t('inventory.move_right_tooltip')}
                          >
                            ▶
                          </button>
                          <button
                            className="pm-column-btn visibility-btn"
                            onClick={() => setShowColumnMenu(showColumnMenu === col.key ? null : col.key)}
                            title={t('inventory.column_options_tooltip')}
                          >
                            ⋮
                          </button>
                        </div>
                        <div className="pm-header-label" onClick={() => handleSort(col.key)}>
                          {col.label}
                          {sortConfig.key === col.key && (
                            <span className="pm-sort-indicator">
                              {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                            </span>
                          )}
                        </div>
                      </div>
                      {showColumnMenu === col.key && (
                        <div className="pm-column-menu">
                          <button onClick={() => {
                            toggleColumnVisibility(col.key);
                            setShowColumnMenu(null);
                          }}>
                            {t('inventory.hide_column')}
                          </button>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1}>
                      <div className="pm-skeleton" aria-busy="true">{t('inventory.loading_product_suppliers')}</div>
                    </td>
                  </tr>
                ) : (
                  filteredProductSuppliers.map((ps) => (
                    <tr key={ps.id}>
                      <td>
                        <div className="pm-row-actions">
                          <button className="pm-row-btn edit" onClick={() => handleOpenModal(ps)} title={t('inventory.edit_tooltip')}>
                            <span aria-hidden="true">✏️</span>
                          </button>
                          <button className="pm-row-btn delete" onClick={() => handleDelete(ps.id)} title={t('inventory.delete_tooltip')}>
                            <span aria-hidden="true">🗑️</span>
                          </button>
                        </div>
                      </td>
                      {visibleColumns.map(col => (
                        <td key={col.key}>
                          {formatCellValue(ps[col.key], col.key)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="pm-modal-overlay">
          <div className="pm-modal-content">
            <div className="pm-modal-header">
              <h2>{editingId ? t('inventory.edit_product_supplier_title') : t('inventory.add_supplier_to_product_title')}</h2>
              <button className="pm-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-form-grid">
                <div className="pm-form-group">
                  <label htmlFor="ps-supplier">{t('inventory.supplier_label')}</label>
                  <select
                    id="ps-supplier"
                    value={editFormData.supplier_id}
                    onChange={(e) => setEditFormData({ ...editFormData, supplier_id: e.target.value })}
                  >
                    <option value="">{t('inventory.select_supplier_option')}</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pm-form-group">
                  <label htmlFor="ps-sku">{t('inventory.supplier_sku_label')}</label>
                  <input
                    id="ps-sku"
                    type="text"
                    value={editFormData.supplier_sku}
                    onChange={(e) => setEditFormData({ ...editFormData, supplier_sku: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="ps-price">{t('inventory.unit_price_label')}</label>
                  <input
                    id="ps-price"
                    type="number"
                    step="0.01"
                    value={editFormData.unit_price}
                    onChange={(e) => setEditFormData({ ...editFormData, unit_price: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="ps-lead">{t('inventory.lead_time_days_label')}</label>
                  <input
                    id="ps-lead"
                    type="number"
                    value={editFormData.lead_time_days}
                    onChange={(e) => setEditFormData({ ...editFormData, lead_time_days: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="ps-min">{t('inventory.min_order_qty_label')}</label>
                  <input
                    id="ps-min"
                    type="number"
                    value={editFormData.min_order_qty}
                    onChange={(e) => setEditFormData({ ...editFormData, min_order_qty: e.target.value })}
                  />
                </div>

                <div className="pm-toggle-row">
                  <label htmlFor="ps-preferred">
                    <input
                      id="ps-preferred"
                      type="checkbox"
                      role="switch"
                      checked={editFormData.is_preferred}
                      onChange={(e) => setEditFormData({ ...editFormData, is_preferred: e.target.checked })}
                    />
                    {t('inventory.preferred_header')}
                  </label>
                  <label htmlFor="ps-active">
                    <input
                      id="ps-active"
                      type="checkbox"
                      role="switch"
                      checked={editFormData.is_active}
                      onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                    />
                    {t('inventory.active_label')}
                  </label>
                </div>
              </div>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn-secondary" onClick={handleCloseModal} type="button">
                {t('inventory.cancel_button')}
              </button>
              <button className="pm-btn-primary" onClick={handleSave} type="button">
                {editingId ? t('inventory.update_category_button') : t('inventory.create_category_button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSuppliers;
